<template>
  <div class="sales-dashboard">
    <!-- Top Header with Brand Identity -->
    <div class="dashboard-header">
      <div class="header-content">
        <div class="brand-section">
          <div class="brand-icon">
            <feather-icon
              icon="TrendingUpIcon"
              size="32"
              class="brand-logo"
            />
          </div>
          <div class="brand-info">
            <h1 class="brand-title">
              {{ $branding.company.name }}
            </h1>
            <p class="brand-subtitle">
              Tableau de bord Commercial
            </p>
          </div>
        </div>

        <div class="header-actions">
          <div class="time-display">
            <feather-icon
              icon="ClockIcon"
              size="16"
              class="time-icon"
            />
            <span class="current-time">{{ getCurrentTime() }}</span>
          </div>
          <b-button
            variant="outline-light"
            size="sm"
            class="refresh-btn"
            :disabled="loading"
            @click="loadDashboard"
          >
            <feather-icon
              icon="RefreshCwIcon"
              size="16"
              :class="{ 'spinning': loading }"
            />
            {{ loading ? 'Chargement...' : 'Actualiser' }}
          </b-button>
        </div>
      </div>
    </div>

    <!-- Loading Overlay -->
    <div
      v-if="loading"
      class="loading-overlay"
    >
      <div class="loading-content">
        <div class="loading-spinner" />
        <p class="loading-text">
          Chargement des données...
        </p>
      </div>
    </div>

    <!-- Summary Cards -->
    <div
      v-if="!loading"
      class="summary-cards"
    >
      <div class="summary-card commission">
        <div class="card-icon">
          <feather-icon
            icon="DollarSignIcon"
            size="28"
          />
        </div>
        <div class="card-content">
          <div class="card-label">
            Commission Totale
          </div>
          <div class="card-value">
            {{ formatAmount(dashboard.total_commission_earned) }} DT
          </div>
        </div>
      </div>

      <div class="summary-card orders">
        <div class="card-icon">
          <feather-icon
            icon="PackageIcon"
            size="28"
          />
        </div>
        <div class="card-content">
          <div class="card-label">
            Commandes Livrées
          </div>
          <div class="card-value">
            {{ dashboard.total_delivered_orders }}
          </div>
        </div>
      </div>

      <div class="summary-card suppliers">
        <div class="card-icon">
          <feather-icon
            icon="UsersIcon"
            size="28"
          />
        </div>
        <div class="card-content">
          <div class="card-label">
            Fournisseurs Attribués
          </div>
          <div class="card-value">
            {{ dashboard.total_suppliers }}
          </div>
        </div>
      </div>

      <div class="summary-card pending">
        <div class="card-icon">
          <feather-icon
            icon="ClockIcon"
            size="28"
          />
        </div>
        <div class="card-content">
          <div class="card-label">
            Commission en Attente
          </div>
          <div class="card-value">
            {{ formatAmount(dashboard.pending_invoice_amount) }} DT
          </div>
        </div>
      </div>
    </div>

    <!-- Current Period Card -->
    <div
      v-if="!loading && dashboard.current_period"
      class="current-period-section"
    >
      <div class="period-card">
        <div class="period-header">
          <h3>
            <feather-icon
              icon="CalendarIcon"
              size="20"
            />
            Période en cours
          </h3>
          <span class="period-dates">
            {{ formatDate(dashboard.current_period.start_date) }} - {{ formatDate(dashboard.current_period.end_date) }}
          </span>
        </div>
        <div class="period-stats">
          <div class="stat">
            <span class="stat-label">Taux de commission</span>
            <span class="stat-value">{{ formatAmount(dashboard.current_period.rate) }} DT/commande</span>
          </div>
          <div class="stat">
            <span class="stat-label">Commandes livrées</span>
            <span class="stat-value">{{ dashboard.current_period.total_orders }}</span>
          </div>
          <div class="stat">
            <span class="stat-label">Commission de la période</span>
            <span class="stat-value highlight">{{ formatAmount(dashboard.current_period.total_commission) }} DT</span>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="!loading && !dashboard.current_period"
      class="no-period-notice"
    >
      <feather-icon
        icon="AlertCircleIcon"
        size="24"
      />
      <p>Aucune période de commission active pour le moment.</p>
    </div>

    <!-- Main Content Grid -->
    <div
      v-if="!loading"
      class="content-grid"
    >
      <!-- Suppliers List -->
      <div class="content-card">
        <h3>
          <feather-icon
            icon="UsersIcon"
            size="20"
          />
          Fournisseurs Attribués
        </h3>
        <div
          v-if="dashboard.suppliers && dashboard.suppliers.length > 0"
          class="suppliers-list"
        >
          <div
            v-for="supplier in dashboard.suppliers"
            :key="supplier.supplier_id"
            class="supplier-item"
          >
            <div class="supplier-info">
              <span class="supplier-name">{{ supplier.supplier_name }}</span>
              <span class="supplier-phone">{{ supplier.supplier_phone }}</span>
            </div>
            <div class="supplier-stats">
              <span class="delivered-count">
                <feather-icon
                  icon="CheckCircleIcon"
                  size="14"
                />
                {{ supplier.delivered_count }} livrées
              </span>
              <span class="total-count">
                / {{ supplier.total_orders }} total
              </span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="no-data-message"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
          />
          <p>Aucun fournisseur attribué</p>
        </div>
      </div>

      <!-- Recent Invoices -->
      <div class="content-card">
        <h3>
          <feather-icon
            icon="FileTextIcon"
            size="20"
          />
          Factures Récentes
        </h3>
        <div
          v-if="dashboard.recent_invoices && dashboard.recent_invoices.length > 0"
          class="invoices-list"
        >
          <div
            v-for="invoice in dashboard.recent_invoices"
            :key="invoice.id"
            class="invoice-item"
          >
            <div class="invoice-info">
              <span class="invoice-number">{{ invoice.invoice_number }}</span>
              <span class="invoice-period">
                {{ formatDate(invoice.period_start) }} - {{ formatDate(invoice.period_end) }}
              </span>
            </div>
            <div class="invoice-details">
              <span class="invoice-amount">{{ formatAmount(invoice.total_amount) }} DT</span>
              <span
                class="invoice-status"
                :class="invoice.payment_status"
              >
                {{ invoice.payment_status_display }}
              </span>
            </div>
          </div>
        </div>
        <div
          v-else
          class="no-data-message"
        >
          <feather-icon
            icon="InboxIcon"
            size="48"
          />
          <p>Aucune facture</p>
        </div>
      </div>
    </div>

    <!-- Commission Periods History -->
    <div
      v-if="!loading && dashboard.periods && dashboard.periods.length > 0"
      class="periods-section"
    >
      <div class="content-card full-width">
        <h3>
          <feather-icon
            icon="BarChart2Icon"
            size="20"
          />
          Historique des Périodes de Commission
        </h3>
        <table class="periods-table">
          <thead>
            <tr>
              <th>Période</th>
              <th class="text-center">
                Taux
              </th>
              <th class="text-center">
                Commandes
              </th>
              <th class="text-right">
                Commission
              </th>
              <th class="text-center">
                Facture
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="period in dashboard.periods"
              :key="period.period_id"
            >
              <td>
                {{ formatDate(period.start_date) }} - {{ formatDate(period.end_date) }}
              </td>
              <td class="text-center">
                {{ formatAmount(period.rate) }} DT
              </td>
              <td class="text-center">
                {{ period.total_orders }}
              </td>
              <td class="text-right amount-cell">
                {{ formatAmount(period.total_commission) }} DT
              </td>
              <td class="text-center">
                <span
                  v-if="period.has_invoice"
                  class="invoice-badge"
                  :class="period.invoice_status"
                >
                  {{ period.invoice_status === 'paid' ? 'Payée' : 'En attente' }}
                </span>
                <span
                  v-else
                  class="no-invoice-badge"
                >
                  Non facturée
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { BButton } from 'bootstrap-vue'

export default {
  name: 'SalesDashboard',
  components: {
    BButton,
  },
  data() {
    return {
      loading: false,
      dashboard: {
        current_period: null,
        periods: [],
        suppliers: [],
        recent_invoices: [],
        total_delivered_orders: 0,
        total_commission_earned: 0,
        total_suppliers: 0,
        pending_invoice_amount: 0,
      },
    }
  },
  mounted() {
    this.loadDashboard()
  },
  methods: {
    async loadDashboard() {
      this.loading = true
      try {
        const response = await axios.get('/api/commissions/dashboard/')
        this.dashboard = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading dashboard:', error)
        this.$bvToast.toast('Erreur lors du chargement du tableau de bord', {
          title: 'Erreur',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.loading = false
      }
    },
    formatAmount(value) {
      if (value === null || value === undefined) return '0.000'
      return parseFloat(value).toFixed(3)
    },
    formatDate(dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },
    getCurrentTime() {
      return new Date().toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.sales-dashboard {
  min-height: 100vh;
  background: #f8f9fa;
}

/* Header */
.dashboard-header {
  background: linear-gradient(135deg, #3498db 0%, #2980b9 100%);
  padding: 24px;
  margin-bottom: 24px;
  border-radius: 0 0 24px 24px;
  box-shadow: 0 4px 20px rgba(52, 152, 219, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.brand-section {
  display: flex;
  align-items: center;
  gap: 16px;
}

.brand-icon {
  width: 60px;
  height: 60px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.brand-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: white;
}

.brand-subtitle {
  margin: 4px 0 0 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.9);
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.time-display {
  display: flex;
  align-items: center;
  gap: 8px;
  color: rgba(255, 255, 255, 0.9);
  font-size: 14px;
}

.refresh-btn {
  border-color: rgba(255, 255, 255, 0.5);
  color: white;
}

.refresh-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: white;
}

.spinning {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Loading */
.loading-overlay {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 60px 20px;
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid #e5e7eb;
  border-top-color: #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

.loading-text {
  color: #6b7280;
  font-size: 16px;
}

/* Summary Cards */
.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 20px;
  padding: 0 24px;
  margin-bottom: 24px;
}

.summary-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  display: flex;
  align-items: center;
  gap: 16px;
}

.card-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-card.commission .card-icon {
  background: #d1fae5;
  color: #059669;
}

.summary-card.orders .card-icon {
  background: #dbeafe;
  color: #2563eb;
}

.summary-card.suppliers .card-icon {
  background: #fef3c7;
  color: #d97706;
}

.summary-card.pending .card-icon {
  background: #fee2e2;
  color: #dc2626;
}

.card-label {
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 4px;
}

.card-value {
  font-size: 24px;
  font-weight: 700;
  color: #1f2937;
}

/* Current Period */
.current-period-section {
  padding: 0 24px;
  margin-bottom: 24px;
}

.period-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #3498db;
}

.period-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.period-header h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 18px;
  color: #1f2937;
}

.period-dates {
  color: #6b7280;
  font-size: 14px;
}

.period-stats {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
}

.stat {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 14px;
  color: #6b7280;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.stat-value.highlight {
  color: #3498db;
  font-size: 24px;
}

.no-period-notice {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 24px;
  margin: 0 24px 24px;
  background: #fef3c7;
  border-radius: 8px;
  color: #92400e;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
  gap: 20px;
  padding: 0 24px;
  margin-bottom: 24px;
}

.content-card {
  background: white;
  padding: 24px;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.content-card.full-width {
  grid-column: 1 / -1;
}

.content-card h3 {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 20px 0;
  font-size: 18px;
  color: #1f2937;
}

/* Suppliers List */
.suppliers-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.supplier-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 8px;
}

.supplier-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.supplier-name {
  font-weight: 600;
  color: #1f2937;
}

.supplier-phone {
  font-size: 13px;
  color: #6b7280;
}

.supplier-stats {
  display: flex;
  align-items: center;
  gap: 8px;
}

.delivered-count {
  display: flex;
  align-items: center;
  gap: 4px;
  color: #059669;
  font-weight: 500;
}

.total-count {
  color: #9ca3af;
  font-size: 13px;
}

/* Invoices List */
.invoices-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.invoice-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f9fafb;
  border-radius: 8px;
}

.invoice-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.invoice-number {
  font-weight: 600;
  color: #1f2937;
}

.invoice-period {
  font-size: 13px;
  color: #6b7280;
}

.invoice-details {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
}

.invoice-amount {
  font-weight: 600;
  color: #1f2937;
}

.invoice-status {
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 4px;
  font-weight: 500;
}

.invoice-status.paid {
  background: #d1fae5;
  color: #059669;
}

.invoice-status.unpaid {
  background: #fee2e2;
  color: #dc2626;
}

/* Periods Section */
.periods-section {
  padding: 0 24px;
  margin-bottom: 24px;
}

.periods-table {
  width: 100%;
  border-collapse: collapse;
}

.periods-table th {
  text-align: left;
  padding: 12px;
  background: #f9fafb;
  color: #6b7280;
  font-weight: 600;
  font-size: 14px;
  border-bottom: 2px solid #e5e7eb;
}

.periods-table td {
  padding: 12px;
  border-bottom: 1px solid #f3f4f6;
  font-size: 14px;
}

.periods-table tbody tr:hover {
  background: #f9fafb;
}

.text-right {
  text-align: right;
}

.text-center {
  text-align: center;
}

.amount-cell {
  font-weight: 600;
  color: #3498db;
  font-family: 'Courier New', monospace;
}

.invoice-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
}

.invoice-badge.paid {
  background: #d1fae5;
  color: #059669;
}

.invoice-badge.unpaid {
  background: #fee2e2;
  color: #dc2626;
}

.no-invoice-badge {
  display: inline-block;
  padding: 4px 12px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  background: #f3f4f6;
  color: #6b7280;
}

/* No Data */
.no-data-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #9ca3af;
}

.no-data-message p {
  margin: 12px 0 0 0;
  font-size: 14px;
}

/* Responsive */
@media (max-width: 768px) {
  .dashboard-header {
    padding: 20px;
    border-radius: 0;
  }

  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }

  .summary-cards,
  .content-grid,
  .periods-section,
  .current-period-section {
    padding: 0 16px;
  }

  .content-grid {
    grid-template-columns: 1fr;
  }
}
</style>
